import { Alert, AlertIcon, Box, Flex, Spinner, Text } from "@chakra-ui/react";
import React from "react";
import {
  UseFieldArrayAppend,
  UseFieldArrayMove,
  UseFieldArrayRemove,
} from "react-hook-form";
import { HiOutlineSparkles } from "react-icons/hi2";

import { Button } from "../../../components";
import { AiInterviewQuestion } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import AiInterviewerGuideQuestions from "./AiInterviewerGuideQuestions";
import AiInterviewerSection from "./AiInterviewerSection";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import AiInterviewerGuideAssistant from "./GuideAssistantV2/AiInterviewerGuideAssistant";
import { AiInterviewerFormValues } from "./types";
import useAnalyzeGuide from "./useAnalyzeGuide";

type AiInterviewerGuideProps = {
  readOnly?: boolean;
  questions: AiInterviewQuestion[];
  appendQuestion: UseFieldArrayAppend<AiInterviewerFormValues, "questions">;
  removeQuestion: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  onAcceptSuggestion: (suggestionId: string) => void;
};

const AiInterviewerGuide: React.FC<AiInterviewerGuideProps> = ({
  readOnly,
  questions,
  appendQuestion,
  removeQuestion,
  move,
  pendingAcceptedSuggestionIds,
  onAcceptSuggestion,
}) => {
  const isV2FeatureEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:suggestionsV2:frontend"
  );
  const {
    analysisResult,
    isPanelOpen,
    openPanel,
    closePanel,
    loading: analyzeLoading,
    error: analyzeError,
    isFeatureEnabled,
  } = useAnalyzeGuide();

  if (isV2FeatureEnabled) {
    return (
      <AiInterviewerGuideAssistant
        readOnly={readOnly}
        questions={questions}
        appendQuestion={appendQuestion}
        removeQuestion={removeQuestion}
        move={move}
        pendingAcceptedSuggestionIds={pendingAcceptedSuggestionIds}
        onAcceptSuggestion={onAcceptSuggestion}
      />
    );
  }

  const hasAnalysis = analysisResult !== null;

  return (
    <Flex flexDirection="column" alignItems="center">
      <AiInterviewerSection
        w={isPanelOpen && hasAnalysis ? "1200px" : "720px"}
        my="4"
        transition="width 0.2s ease-in-out"
      >
        <AiInterviewerSectionTitle
          title={`Interview guide (${questions.length})`}
          rightContent={
            <Flex align="center" gap={3}>
              {!readOnly && isFeatureEnabled && (
                <>
                  {analyzeLoading ? (
                    <Flex align="center" gap={2} color="purple.500">
                      <Spinner size="sm" />
                      <Text fontSize="sm">
                        <HiOutlineSparkles
                          style={{ display: "inline", marginRight: "4px" }}
                        />
                        Analyzing Guide...
                      </Text>
                    </Flex>
                  ) : hasAnalysis && isPanelOpen ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={closePanel}
                      colorScheme="gray"
                    >
                      Close Analysis
                    </Button>
                  ) : hasAnalysis && !isPanelOpen ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openPanel}
                      colorScheme="purple"
                      leftIcon={<HiOutlineSparkles />}
                    >
                      View Suggestions
                    </Button>
                  ) : null}
                </>
              )}
            </Flex>
          }
        />
        {analyzeError && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {analyzeError}
          </Alert>
        )}
        {isPanelOpen && hasAnalysis ? (
          <Box overflow="hidden">
            <AiInterviewerGuideQuestions
              readOnly={readOnly}
              questions={questions}
              appendQuestion={appendQuestion}
              removeQuestion={removeQuestion}
              move={move}
              analysisResult={analysisResult}
              reservePanelSpace
            />
          </Box>
        ) : (
          <AiInterviewerGuideQuestions
            readOnly={readOnly}
            questions={questions}
            appendQuestion={appendQuestion}
            removeQuestion={removeQuestion}
            move={move}
          />
        )}
      </AiInterviewerSection>
    </Flex>
  );
};

export default AiInterviewerGuide;
